/**
 * Where the player's session lives.
 *
 * Normally the session token is kept in localStorage per room (§16.4: reopening the page resumes
 * the game). The dev table harness runs many clients in iframes of one origin, which share
 * localStorage, so it hands each tile its session in the URL (`?s=`) instead; such a session
 * stays in memory and is never written to storage.
 */
import { readJson, readLocal, writeLocal } from "./storage.ts";
import type { Session } from "./types.ts";

const KEY = (code: string) => `handshake:session:${code.toUpperCase()}`;
const LAST = "handshake:last-room";

function isSession(x: unknown): x is Session {
  const s = x as Session | null;
  return (
    !!s &&
    typeof s.code === "string" &&
    typeof s.playerId === "string" &&
    typeof s.token === "string"
  );
}

export function encodeSessionParam(session: Session): string {
  return btoa(JSON.stringify(session)).replace(/=+$/, "");
}

export function decodeSessionParam(param: string | null): Session | null {
  if (!param) return null;
  try {
    const s: unknown = JSON.parse(atob(param));
    return isSession(s) ? s : null;
  } catch {
    return null;
  }
}

/** The session handed in by the dev harness, if any (in memory only). */
export function embeddedSession(): Session | null {
  if (typeof window === "undefined") return null;
  return decodeSessionParam(new URLSearchParams(window.location.search).get("s"));
}

export function loadSession(code: string): Session | null {
  const embedded = embeddedSession();
  if (embedded) return embedded.code === code.toUpperCase() ? embedded : null;
  const s = readJson<unknown>(KEY(code));
  return isSession(s) ? s : null;
}

export function saveSession(session: Session): void {
  if (embeddedSession()) return;
  writeLocal(KEY(session.code), JSON.stringify(session));
  writeLocal(LAST, session.code);
}

export function forgetSession(code: string): void {
  if (embeddedSession()) return;
  writeLocal(KEY(code), null);
}

export function lastRoom(): string | null {
  const code = readLocal(LAST);
  if (code && loadSession(code)) return code;
  return null;
}
